import React, { Suspense, useEffect } from "react";
import { Route, BrowserRouter as Router, Routes } from "react-router-dom";
import { Box, Spinner } from "@chakra-ui/react";
import AppWrapper from "./components/layout/AppWrapper";
import CheckInformation from "./pages/CheckInformation";
import Layout from "./components/layout";
import CompanyWrapper from "./components/layout/CompanyWrapper";
import { recordAppVisit } from "./utils/recentApps";
import { lazyWithRetry, SessionProvider } from "./shared/session";
import { sessionConfig } from "./session";

// Pages load on demand: page-only libraries (calendar, date pickers, phone
// input, ...) stay out of the initial bundle. "/" (CheckInformation) and the
// layout shell stay eager since they're on the first paint path.
const loadDashboard = () => import("./pages/Dashboard");
const DashboardPage = lazyWithRetry(loadDashboard);
const EquipmentAvailabilityListing = lazyWithRetry(() => import("./pages/Equipment"));
const EquipmentDetailsPage = lazyWithRetry(
  () => import("./pages/Equipment/MobileEquipmentDetails")
);
const DriversListing = lazyWithRetry(() => import("./pages/Driver"));
const DriverCalendarPage = lazyWithRetry(() => import("./pages/Driver/DriverCalendar"));
const ManageBids = lazyWithRetry(() => import("./pages/Bids"));
const BidSettings = lazyWithRetry(() => import("./pages/Bids/BidSettings"));
const BidDetails = lazyWithRetry(() => import("./pages/Bids/BidDetails"));
const LoadsPage = lazyWithRetry(() => import("./pages/Loads"));
const ChatPage = lazyWithRetry(() => import("./pages/Chats"));
const NoPage = lazyWithRetry(() => import("./pages/404"));

// same full-height spinner the app shows while it checks the session / company
export const PageFallback = () => (
  <Box
    h="100vh"
    w="100%"
    display="flex"
    alignItems="center"
    justifyContent="center"
  >
    <Spinner />
  </Box>
);

// Define route config
const routes = [
  { path: "/", element: <CheckInformation />, layout: false },
  {
    path: "/equipment-availability",
    element: <EquipmentAvailabilityListing />,
  },
  {
    path: "/equipment-availability/:equipmentID",
    element: <EquipmentDetailsPage />,
  },
  { path: "/driver-schedule", element: <DriversListing /> },
  { path: "/driver-schedule/:userID", element: <DriverCalendarPage /> },
  { path: "/manage-bids", element: <ManageBids /> },
  { path: "/load-execution", element: <LoadsPage /> },
  { path: "/chats", element: <ChatPage /> },
  { path: "/manage-bids/details", element: <BidDetails /> },
  { path: "/bid-settings", element: <BidSettings /> },
  { path: "/dashboard", element: <DashboardPage /> },
  {path: "/*", element: <NoPage/>, layout: false}
];

export const MainRoute: React.FC = () => {
  return (
    <Routes>
      {routes.map(({ path, element, layout = true }) => (
        <Route
          key={path}
          path={path}
          element={
            layout ? (
              <Layout>
                <Suspense fallback={<PageFallback />}>{element}</Suspense>
              </Layout>
            ) : (
              <Suspense fallback={<PageFallback />}>{element}</Suspense>
            )
          }
        />
      ))}
    </Routes>
  );
};

const App = () => {
  // this app goes to the front of the shared "recently opened" history
  // (the sidebar shortcuts of every app read it)
  useEffect(() => {
    recordAppVisit();
    // "/" only checks the company and then redirects to the dashboard (or the
    // page the user came from): fetch the dashboard chunk meanwhile
    // (a failed prefetch is retried when the route renders)
    if (window.location.pathname === "/") loadDashboard().catch(() => {});
  }, []);

  return (
    <React.Fragment>
      <Router>
        {/* routes render once signed in (cookie / ?token= / silent refresh);
            otherwise the session layer sends the user to sign in */}
        <SessionProvider config={sessionConfig} fallback={<PageFallback />}>
          <AppWrapper>
            <CompanyWrapper>
              <MainRoute />
            </CompanyWrapper>
          </AppWrapper>
        </SessionProvider>
      </Router>
    </React.Fragment>
  );
};

export default App;
